import { Client } from "../../model/client/Client";
import i18n from "../../i18n";
import { ClientInput } from "./ClientInput";
import { ClientNote, ClientNoteInput } from "../../model/client/ClientNote";

const API_URL = process.env.REACT_APP_CLIENT_SERVICE_BASE_URL || "";

const CLIENT_NOTE_FIELDS = `
    id
    businessId
    clientId
    teamMemberId
    note
    createdBy
    createdOn
    updatedOn
`;

const CLIENT_FIELDS = `
    id
    businessId
    firstName
    lastName
    email
    phone
    notes
    discountPercent
    active
    favorite
    cardBrand
    cardLast4
    cardExpMonth
    cardExpYear
    hasCardOnFile
    registered
    avatar
    birthDay
    birthMonth
    birthYear
    convenios {
        id
        healthPlanId
        cardNumber
        validThrough
        holderType
        holderName
    }
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(", ");
        throw new Error(msg);
    }
    return json.data as T;
}

export async function getAllClientsByBusinessId(
    businessId: string,
    token: string
): Promise<Client[]> {
    const data = await gqlPost<{ getAllClientsByBusinessId: Client[] | null }>(
        token,
        `query getAllClientsByBusinessId($businessId: ID!) {
            getAllClientsByBusinessId(businessId: $businessId) { ${CLIENT_FIELDS} }
        }`,
        { businessId }
    );
    return data.getAllClientsByBusinessId ?? [];
}

/** Contact-only booking lookup — gated by CALENDAR (not CLIENTS). Returns only
 *  id/name/phone/email so a professional can find a client to schedule without
 *  the sensitive client data. Query needs >= 2 chars; capped at 20 results. */
export type BookingClient = { id: string; firstName: string; lastName?: string | null; phone?: string | null; email?: string | null };
export async function bookingClientLookup(
    businessId: string,
    query: string,
    token: string
): Promise<BookingClient[]> {
    const data = await gqlPost<{ bookingClientLookup: BookingClient[] | null }>(
        token,
        `query bookingClientLookup($businessId: ID!, $query: String) {
            bookingClientLookup(businessId: $businessId, query: $query) { id firstName lastName phone email }
        }`,
        { businessId, query }
    );
    return data.bookingClientLookup ?? [];
}

/** Substring match across firstName/lastName/email/phone. Empty query returns all. */
export async function searchClients(
    businessId: string,
    query: string,
    token: string
): Promise<Client[]> {
    const data = await gqlPost<{ searchClients: Client[] | null }>(
        token,
        `query searchClients($businessId: ID!, $query: String) {
            searchClients(businessId: $businessId, query: $query) { ${CLIENT_FIELDS} }
        }`,
        { businessId, query }
    );
    return data.searchClients ?? [];
}

export async function saveClient(input: ClientInput, token: string): Promise<Client> {
    const data = await gqlPost<{ saveClient: Client }>(
        token,
        `mutation saveClient($input: ClientInput!) {
            saveClient(input: $input) { ${CLIENT_FIELDS} }
        }`,
        { input }
    );
    return data.saveClient;
}

/**
 * If an inactive client with the same email OR phone exists, returns it so the
 * UI can offer reactivation instead of creating a duplicate. Returns null when
 * no match.
 */
export async function findInactiveClientByContact(
    businessId: string,
    email: string | null,
    phone: string | null,
    token: string
): Promise<Client | null> {
    if (!email && !phone) return null;
    const data = await gqlPost<{ findInactiveClientByContact: Client | null }>(
        token,
        `query findInactiveClientByContact($businessId: ID!, $email: String, $phone: String) {
            findInactiveClientByContact(businessId: $businessId, email: $email, phone: $phone) { ${CLIENT_FIELDS} }
        }`,
        { businessId, email: email || null, phone: phone || null }
    );
    return data.findInactiveClientByContact ?? null;
}

/** Mark/unmark a client as favorite (CLIENTS edit). Returns the updated client. */
export async function setClientFavorite(
    id: string,
    favorite: boolean,
    token: string
): Promise<Client> {
    const data = await gqlPost<{ setClientFavorite: Client }>(
        token,
        `mutation setClientFavorite($id: ID!, $favorite: Boolean!) {
            setClientFavorite(id: $id, favorite: $favorite) { ${CLIENT_FIELDS} }
        }`,
        { id, favorite }
    );
    return data.setClientFavorite;
}

export async function deleteClient(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteClient: boolean }>(
        token,
        `mutation deleteClient($id: ID!) { deleteClient(id: $id) }`,
        { id }
    );
    return !!data.deleteClient;
}

// ---------------------------------------------------------------------------
// Client notes / annotations (the "Anotações" tab). Gated by CLIENT_HISTORY:
// VIEW lists (own-scope returns only the caller's own notes); EDIT saves/deletes.
// ---------------------------------------------------------------------------

export async function getClientNotes(clientId: string, token: string): Promise<ClientNote[]> {
    const data = await gqlPost<{ getClientNotes: ClientNote[] | null }>(
        token,
        `query getClientNotes($clientId: ID!) {
            getClientNotes(clientId: $clientId) { ${CLIENT_NOTE_FIELDS} }
        }`,
        { clientId }
    );
    return data.getClientNotes ?? [];
}

export async function saveClientNote(input: ClientNoteInput, token: string): Promise<ClientNote> {
    const data = await gqlPost<{ saveClientNote: ClientNote }>(
        token,
        `mutation saveClientNote($input: ClientNoteInput!) {
            saveClientNote(input: $input) { ${CLIENT_NOTE_FIELDS} }
        }`,
        { input }
    );
    return data.saveClientNote;
}

export async function deleteClientNote(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteClientNote: boolean }>(
        token,
        `mutation deleteClientNote($id: ID!) { deleteClientNote(id: $id) }`,
        { id }
    );
    return !!data.deleteClientNote;
}
